<template>
  <NuxtLayout name="sticky" shamrock>
    <template #header>
      <ScratchcardHeader with-fly-out @click="navigateToLocalizedBackroute" />
    </template>

    <ScratchcardQuantity data-containerid="scratch_editor" />
    <ScratchcardSoldOutModal
      :is-visible="(!gameLoading && gameNotAvailable) || isReservationNotPossible"
    />
    <TheMetaLinks class="mt-300" />

    <template #navbar>
      <LcButton class="w-full" :disabled="loading" @click="checkout">
        {{ $t('scratchcard.navbar.label') }}
      </LcButton>
    </template>

    <CheckoutRestrictions
      v-if="restrictions"
      v-bind="{ grossTotal, balance, restrictions }"
      close-all-modals-on-confirm
    />

    <ComplianceLocationPermissionModal
      :is-visible="showLocationPermissionModal"
      tracking-action="checkout"
      @cancel="showLocationPermissionModal = false"
    />

    <AccountVerificationModalControl
      :is-visible="showIsNotVerifiedModal"
      :redirect="
        localePath(
          `/checkout?p=${slug}&scratchcard=1&quantity=${selectedQuantity}&backroute=/scratchcard/${slug}`,
        )
      "
      @close="showIsNotVerifiedModal = false"
    />
  </NuxtLayout>
</template>

<script lang="ts" setup>
import type { ApolloError } from '@apollo/client'
import type { ScratchcardPriceBreakdown } from '@lottocom/price-calculation'
import type { GraphQLFormattedError } from 'graphql'

import Money from '~~/models/Money'

import {
  KnownProductType,
  type Restrictions,
} from '#api/shared/@types/generated/backend/graphql-schema-types'
import { useProductStore } from '#product/app/stores/product'
import { MIN_SELECTED_QUANTITY, useScratchcardStore } from '#scratchcard/app/stores/scratchcard'
import { QUERY_REVALIDATE } from '~/constants/queryParams'

const { navigateToLocalized, navigateToLocalizedBackroute } = useRouterUtils()
const localePath = useLocalePath()

const isNotFound = ({ message }: GraphQLFormattedError) => message === 'validation.game.notfound'

const isUnavailableGameError = ({ message }: GraphQLFormattedError) =>
  message.includes('unavailable.game.')

definePageMeta({
  isGameRoute: true,
  layout: false,
  middleware: ['age-verification', 'self-excluded'],
  tracking: { name: 'ScratchcardEditor' },
  validate: () => useIsScratchcardEnabled(),
})

const route = useRoute()
const { removeQueryParams } = useRouterUtils()
const nuxt = useNuxtApp()
const { t } = useI18n()
const { addToastError } = useToaster()
const { log } = useDatadog()
const { isLoggedIn } = useAuth()
const { logTrackingError } = useTrackingErrorLog()
const { createECommerceEventData } = useTrackingData()
const { pushAddToCartEvent, pushBeginCheckoutEvent, pushRemoveFromCartEvent, pushViewItemEvent } =
  useTracking()

const licensedTerritory = useLicensedTerritory({ raw: true })
const slug = useRoute().params.slug.toString()

const {
  loading: gameLoading,
  onError,
  onResult,
} = useScratchcardGameQuery({ licensedTerritory, slug }, { fetchPolicy: 'network-only' })

const { load: loadAccount, refetch: refetchAccount } = useAccountLazyQuery()

const { game, priceList } = useScratchcardGame()

const store = useScratchcardStore()
const { currentSlug, selectedQuantity } = storeToRefs(store)
const { currentPrice, setCurrentSlug, setSelectedQuantity } = store

const { setProduct } = useProductStore()

const grossTotal = computed(() => currentPrice(priceList.value)?.grossTotal ?? new Money(0))

const available = computed(() => game.value?.available)

const gameNotAvailable = computed(() => !game.value?.enabled || (available.value ?? 0) <= 0)

const balance = ref(new Money(0))

const isReservationNotPossible = ref(false)

const restrictions = ref<Maybe<Restrictions>>(null)

const showIsNotVerifiedModal = ref(false)

const showLocationPermissionModal = ref(false)

const { loading, mutate } = useReserveScratchcardsMutation()

onResult(({ data: { scratchcardGame } }) => {
  if (scratchcardGame.available < selectedQuantity.value && !import.meta.server) {
    setSelectedQuantity(scratchcardGame.available || MIN_SELECTED_QUANTITY)
  }
})

const checkout = async () => {
  await trackAddToCartEvent(true)
  await trackBeginCheckoutEvent()

  if (!isLoggedIn.value) return redirectToLogin()

  const account = (await loadOrRefetch(loadAccount, refetchAccount))?.account
  if (account) balance.value = new Money(account.balance.value / 100)

  try {
    /*
     * (TODO) handle restrictions after Vue3 release
     * https://lottocom.atlassian.net/browse/MIRA-7804 needed
     * see https://lottocom.atlassian.net/browse/SF-4289
     */
    const result = await mutate({
      input: {
        gameSlug: slug,
        licensedTerritory,
        quantity: selectedQuantity.value,
      },
    })

    if (
      !result?.data?.reserveScratchcards.reservationId &&
      !result?.data?.reserveScratchcards.restrictions
    ) {
      return
    }

    if (result.data.reserveScratchcards.restrictions) {
      restrictions.value = result.data.reserveScratchcards.restrictions
      return
    }

    if (!result.data.reserveScratchcards.reservationId) return
    setProduct(result.data.reserveScratchcards.reservationId, slug, ProductType.Scratchcard)
  } catch (error) {
    // (TODO) will be part of the restrictions (see above)
    if (
      isSpecificGraphQLError(error, {
        matchMessage: 'validation.player.invalidstatus',
      })
    ) {
      showIsNotVerifiedModal.value = true
      return
    }

    return handleError(error)
  }

  await navigateToCheckout()
}

const handleError = (error: unknown) => {
  const { graphQLErrors } = error as ApolloError

  if (graphQLErrors?.length && !!graphQLErrors?.find(isUnavailableGameError)) {
    isReservationNotPossible.value = true
    return
  }

  addToastError(
    {
      error,
      prefix: 'scratchcard',
      tracking: {
        action: 'checkout',
        topicKey: 'navigate_to_scratchcard_checkout_error',
      },
    },
    {
      fallback: {
        message: t('global.error'),
        type: 'danger',
      },
    },
  )

  log('error', {
    component: 'scratchcard/[slug]/index.vue',
    error,
    message: `Reservation of ${slug} with quantity of ${selectedQuantity.value} failed.`,
  })
}

const navigateToCheckout = () =>
  navigateToLocalized({
    path: '/checkout',
    query: { p: slug, scratchcard: 1 },
  })

const redirectToLogin = () =>
  navigateToLocalized(
    createRedirectLocation(
      {
        path: '/login',
        query: {
          p: slug,
        },
      },
      localePath(
        `/checkout/redirect?p=${slug}&scratchcard=1&quantity=${selectedQuantity.value}&backroute=/scratchcard/${slug}`,
      ),
    ),
    { replace: true },
  )

onError((error) => {
  if (error.graphQLErrors.find(isNotFound)) {
    nuxt.runWithContext(() => showError({ statusCode: 404 }))
  } else {
    addToastError({
      error,
      prefix: 'scratchcard',
      tracking: {
        action: 'checkout',
        topicKey: 'scratchcard_game_error',
      },
    })
    nuxt.runWithContext(() => navigateToLocalized('/play-now'))
  }
})

const hasTrackedViewItemEvent = ref(false)

const generateEventData = async () => {
  if (!game.value || !priceList.value) return null
  return await createECommerceEventData({
    currentPrice: currentPrice(priceList.value) as ScratchcardPriceBreakdown,
    name: game.value.name,
    priceList: priceList.value,
    quantity: selectedQuantity.value,
    type: KnownProductType.Scratchcard,
  })
}

const trackAddToCartEvent = async (nextStep = false) => {
  try {
    pushAddToCartEvent(await generateEventData(), nextStep)
  } catch (err) {
    logTrackingError('add_to_cart', err)
  }
}

const trackBeginCheckoutEvent = async () => {
  try {
    pushBeginCheckoutEvent(await generateEventData())
  } catch (err) {
    logTrackingError('begin_checkout', err)
  }
}

const trackRemoveFromCartEvent = async () => {
  try {
    pushRemoveFromCartEvent(await generateEventData())
  } catch (err) {
    logTrackingError('remove_from_cart', err)
  }
}

const trackOnMount = async () => {
  if (gameNotAvailable.value) return
  if (hasTrackedViewItemEvent.value) return
  if (!currentPrice(priceList.value)) return
  try {
    pushViewItemEvent(await generateEventData())
  } catch (err) {
    logTrackingError('view_item', err)
  }
  await trackAddToCartEvent()
  hasTrackedViewItemEvent.value = true
}

onBeforeMount(() => {
  if (slug === currentSlug.value) return
  setSelectedQuantity(1)
  setCurrentSlug(slug)
})

onMounted(async () => {
  trackOnMount()

  if (route.query[QUERY_REVALIDATE]) {
    await removeQueryParams(QUERY_REVALIDATE)

    if (!isLoggedIn.value) return
    return await checkout()
  }
})

watch([gameNotAvailable, currentPrice], trackOnMount)
watch(
  () => selectedQuantity.value,
  (newValue, oldValue) => {
    const diff = newValue - oldValue
    if (diff === 0) return
    if (diff > 0) {
      trackAddToCartEvent()
    } else {
      trackRemoveFromCartEvent()
    }
  },
)
</script>
